import React, { useState } from "react";
import Attachment from "../Attachment";
import { PhotoViewer } from "./Overlay";
import { attachmentKind, cover, pdfFirstPage, whole } from "./frame";

// How each style draws a saved post, inside the card's photo row. The card keeps
// the byline, the Where / When / What else margin notes, the reactions and the
// comments for every style — a template changes the pictures, nothing else.
//
// Every photo opens big: one large picture, its own line, two plain arrows, a
// large close. Sam, 2026-09-15: it has to be clear for older eyes.

const images = (post) => (post.attachments || []).filter((a) => attachmentKind(a) === "image");
const details = (post) => post.style_details || {};
const linesOf = (post, key) => details(post)[key] || [];

/** Photos a viewer can page through, with the line under each. */
function useBigView(photos, lines, title) {
  const [openAt, setOpenAt] = useState(null);
  const viewer =
    openAt === null ? null : (
      <PhotoViewer photos={photos} lines={lines} title={title} startIndex={openAt} onClose={() => setOpenAt(null)} />
    );
  return [setOpenAt, viewer];
}

function Zoom({ onOpen, index, count, children, className = "" }) {
  return (
    <button type="button" onClick={() => onOpen(index)} aria-label={`Open photo ${index + 1} of ${count} larger`}
            className={"block w-full cursor-zoom-in border-0 bg-transparent p-0 m-0 " + className}>
      {children}
    </button>
  );
}

export function Snapshot({ post }) {
  const photos = images(post);
  const [open, viewer] = useBigView(photos, [], post.caption || "A family photograph");
  let shown = -1;
  return (
    <>
      {(post.attachments || []).map((a, i) => {
        if (attachmentKind(a) !== "image") return <Attachment key={i} attachment={a} />;
        shown += 1;
        const at = shown;
        return (
          <Zoom key={i} onOpen={open} index={at} count={photos.length} className="mb-[3px]">
            <img src={cover(a.url, 1240, 827)} alt="Family photo" loading="lazy"
                 className="block w-full aspect-[3/2] object-cover bg-black/[0.04]" />
          </Zoom>
        );
      })}
      {viewer}
    </>
  );
}

export function Keepsake({ post }) {
  const photos = images(post);
  const [open, viewer] = useBigView(photos, [], post.caption || "A family photograph");
  if (!photos.length) return <Snapshot post={post} />;
  return (
    <>
      <figure data-testid="keepsake-print"
              className="mx-auto my-[6px] max-w-[420px] bg-[#fffdf6] p-[12px] pb-[14px] shadow-[0_2px_10px_rgba(40,32,8,0.16)]
                         -rotate-1 motion-reduce:rotate-0">
        <Zoom onOpen={open} index={0} count={photos.length}>
          <img src={whole(photos[0].url, 1000)} alt="Family photo" loading="lazy"
               className="block w-full h-auto max-h-[560px] object-contain" />
        </Zoom>
        {post.caption && (
          <figcaption className="pt-[10px] text-center font-marginNote italic text-[17px] leading-[1.3] text-[#3b3526] break-words [overflow-wrap:anywhere]">
            {post.caption}
          </figcaption>
        )}
      </figure>
      {viewer}
    </>
  );
}

export function ThenNow({ post }) {
  const photos = images(post);
  const d = details(post);
  const labels = [`Then${d.then_year ? ` · ${d.then_year}` : ""}`, `Now${d.now_year ? ` · ${d.now_year}` : ""}`];
  const [open, viewer] = useBigView(photos, labels, post.caption || "Then & Now");
  if (photos.length < 2) return <Snapshot post={post} />;
  return (
    <>
      <div className="grid grid-cols-2 gap-[4px] max-[420px]:grid-cols-1">
        {[0, 1].map((i) => (
          <figure key={i} className="relative m-0">
            <Zoom onOpen={open} index={i} count={photos.length}>
              <img src={cover(photos[i].url, 700, 875)} alt={labels[i]} loading="lazy"
                   className="block w-full aspect-[4/5] object-cover bg-black/[0.04]" />
            </Zoom>
            <span className="pointer-events-none absolute left-[8px] bottom-[8px] rounded-[3px] bg-white/95 px-[9px] py-[2px]
                             font-marginNote font-semibold text-[15px] text-dupontBlue">
              <b className="mr-[5px] font-blairBody text-[9px] tracking-[0.14em] uppercase text-dupontGoldDk">
                {i === 0 ? "Then" : "Now"}
              </b>
              {(i === 0 ? d.then_year : d.now_year) || ""}
            </span>
          </figure>
        ))}
      </div>
      {viewer}
    </>
  );
}

export function Clipping({ post }) {
  const [item] = post.attachments || [];
  const [open, viewer] = useBigView(item ? [item] : [], [], post.caption || "A family document");
  if (!item) return null;
  const isPdf = /\.pdf$/i.test(String(item.original_name || item.url).split("?")[0]);
  const kind = attachmentKind(item);
  const picture = isPdf ? pdfFirstPage(item.url) : kind === "image" ? whole(item.url, 1000) : null;
  return (
    <div className="relative bg-[#f3ecd6] dark:bg-neutral-800 px-[18px] py-[16px]
                    [clip-path:polygon(0_0,calc(100%-22px)_0,100%_22px,100%_100%,0_100%)]">
      {picture ? (
        isPdf ? (
          <img src={picture} alt="A family document" loading="lazy"
               className="block mx-auto w-auto max-w-full h-auto max-h-[620px] object-contain bg-white shadow-sm" />
        ) : (
          <Zoom onOpen={open} index={0} count={1}>
            <img src={picture} alt="A family document" loading="lazy"
                 className="block mx-auto w-auto max-w-full h-auto max-h-[620px] object-contain bg-white shadow-sm" />
          </Zoom>
        )
      ) : (
        <p className="font-body italic text-[17px] text-gray-600 dark:text-gray-300 text-center py-[20px] break-words [overflow-wrap:anywhere]">
          {item.original_name || "A family document"}
        </p>
      )}
      {kind === "document" && (
        <a href={item.url} target="_blank" rel="noopener noreferrer"
           className="block mt-[10px] text-center font-blairBody text-[13px] text-dupontBlue dark:text-dupontGoldLt underline underline-offset-[3px]">
          Open the whole document
        </a>
      )}
      {viewer}
    </div>
  );
}

export function Album({ post }) {
  const photos = images(post);
  const lines = linesOf(post, "lines");
  const [open, viewer] = useBigView(photos, lines, post.caption || "Album page");
  if (photos.length < 2) return <Snapshot post={post} />;
  return (
    <>
      <div className="grid grid-cols-2 gap-[6px] max-[420px]:grid-cols-1">
        {photos.map((photo, i) => (
          <figure key={i} className="m-0">
            <Zoom onOpen={open} index={i} count={photos.length}>
              <img src={cover(photo.url, 700, 700)} alt={lines[i] || `Family photo ${i + 1}`} loading="lazy"
                   className="block w-full aspect-square object-cover bg-black/[0.04] rounded-[4px]" />
            </Zoom>
            {lines[i] ? (
              <figcaption className="pt-[5px] pb-[3px] font-marginNote font-semibold text-[15px] leading-[1.25] text-dupontBlue dark:text-darkText break-words [overflow-wrap:anywhere]">
                {lines[i]}
              </figcaption>
            ) : null}
          </figure>
        ))}
      </div>
      {viewer}
    </>
  );
}

export function Placard({ post }) {
  const photos = images(post);
  const d = details(post);
  const [open, viewer] = useBigView(photos, [d.object || ""], d.object || "An heirloom");
  if (!photos.length) return <Snapshot post={post} />;
  return (
    <div className="grid grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] max-[420px]:grid-cols-1 items-end gap-[18px]
                    rounded-[10px] px-[20px] pt-[26px] pb-[18px]
                    bg-[radial-gradient(120%_95%_at_28%_18%,#fdfaf2_0%,#f3ecd6_62%)] dark:bg-neutral-800 dark:bg-none">
      <div className="bg-white p-[7px] shadow-[0_16px_26px_-10px_rgba(40,32,8,0.32)]">
        <Zoom onOpen={open} index={0} count={photos.length}>
          <img src={cover(photos[0].url, 700, 875)} alt={d.object || "An heirloom"} loading="lazy"
               className="block w-full aspect-[4/5] object-cover" />
        </Zoom>
      </div>
      <div className="relative bg-[#fdfcf8] text-[#24211b] px-[14px] pt-[14px] pb-[11px] rounded-[2px] shadow-sm
                      before:absolute before:left-[14px] before:right-[14px] before:top-0 before:h-[2px] before:bg-dupontGold">
        <div className="font-display italic font-semibold text-[18px] leading-[1.15] break-words [overflow-wrap:anywhere]">{d.object}</div>
        {d.made && <div className="font-marginNote font-semibold [font-variant:small-caps] tracking-[0.04em] text-[14px] text-[#6a6454] mt-[2px] break-words [overflow-wrap:anywhere]">{d.made}</div>}
        {d.story && <p className="font-body text-[16px] leading-[1.4] text-[#34302a] mt-[8px] mb-0 break-words [overflow-wrap:anywhere]">{d.story}</p>}
        {d.provenance && (
          <div className="mt-[9px] border-t border-[#ebe5d6] pt-[7px] font-blairBody text-[12px] leading-[1.5] text-[#6a6454] break-words [overflow-wrap:anywhere]">
            {d.provenance}
          </div>
        )}
        {d.collection_no && (
          <div className="mt-[5px] font-blairBody text-[12px] tracking-[0.16em] uppercase text-dupontGoldDk tabular-nums">{d.collection_no}</div>
        )}
      </div>
      {viewer}
    </div>
  );
}

/**
 * One attachment in a fixed frame, whatever kind it is.
 *
 * Did You Know? is the one template that takes documents as well as
 * photographs (Sam, 2026-09-16), so its frames cannot assume a picture.
 * cover() builds a Cloudinary IMAGE transform — pointed at a raw .html upload
 * it returns nothing a browser can draw, and the card shows a broken image.
 */
function AnyFile({ item, onOpen, index, count, round = false }) {
  const kind = attachmentKind(item);
  const name = String(item.original_name || item.url).split("?")[0];
  const isPdf = /\.pdf$/i.test(name);
  const shape = round ? "rounded-full" : "rounded-[6px]";

  if (kind === "image" || isPdf) {
    const src = isPdf ? pdfFirstPage(item.url) : cover(item.url, 260, 260);
    return (
      <Zoom onOpen={onOpen} index={index} count={count}>
        <img src={src} alt={isPdf ? name : ""} loading="lazy"
             className={"block w-full aspect-square object-cover bg-white ring-[3px] ring-white " +
                        "outline outline-1 outline-dupontGold " + (isPdf ? "rounded-[6px]" : shape)} />
      </Zoom>
    );
  }
  // Anything else: say what the file is and let it be opened.
  return (
    <a href={item.url} target="_blank" rel="noopener noreferrer"
       className={"grid aspect-square place-items-center gap-[4px] bg-white dark:bg-neutral-900 p-[8px] " +
                  "text-center outline outline-1 outline-dupontGold " + shape}>
      <span aria-hidden="true" className="font-display text-[26px] leading-none text-dupontGold">☷</span>
      <span className="font-blairBody text-[10px] leading-[1.25] break-all text-dupontBlue dark:text-darkText">
        {name}
      </span>
    </a>
  );
}

export function DidYouKnow({ post }) {
  // Photographs AND documents — this template takes whatever the fact came with.
  const photos = (post.attachments || []).filter((a) => attachmentKind(a) !== "video");
  const [hero, ...rest] = photos;
  const d = details(post);
  const [showMore, setShowMore] = useState(false);
  const [open, viewer] = useBigView(photos, [], d.fact || "Did you know?");
  return (
    <div className="relative overflow-hidden rounded-[10px] bg-dupontCream dark:bg-neutral-800 px-[18px] pt-[18px] pb-[16px]">
      <span aria-hidden="true"
            className="pointer-events-none absolute right-[12px] -top-[26px] font-display italic text-[132px] leading-none text-dupontGold/[0.12]">?</span>
      <div className="relative grid grid-cols-[110px_minmax(0,1fr)] max-[420px]:grid-cols-1 gap-[18px] items-start">
        {hero ? <AnyFile item={hero} onOpen={open} index={0} count={photos.length} round /> : <span />}
        <div>
          <div className="font-marginNote font-semibold [font-variant:small-caps] tracking-[0.06em] text-[15px] text-dupontGoldDk dark:text-dupontGoldLt">
            Did you know?
          </div>
          <p className="font-display font-semibold text-[21px] leading-[1.3] text-dupontBlue dark:text-darkText mt-[4px] mb-[10px] break-words [overflow-wrap:anywhere]">{d.fact}</p>
          {d.more && (
            <button type="button" onClick={() => setShowMore((o) => !o)} aria-expanded={showMore}
                    className="inline-flex items-center gap-[7px] font-blairBody text-[13px] font-semibold text-dupontGoldDk dark:text-dupontGoldLt">
              {showMore ? "Show less" : "Tell me more"}
              <span aria-hidden="true" className={"transition-transform duration-300 motion-reduce:transition-none " + (showMore ? "rotate-180" : "")}>⌄</span>
            </button>
          )}
        </div>
      </div>

      {d.more && (
        <div className={"relative grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none " +
                        (showMore ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
          <div className="overflow-hidden" aria-hidden={!showMore}>
            <p className="font-body text-[18px] leading-[1.5] text-gray-700 dark:text-gray-300 mt-[10px] mb-0 break-words [overflow-wrap:anywhere]">{d.more}</p>
          </div>
        </div>
      )}

      {rest.length > 0 && (
        <div className="relative mt-[14px] grid gap-[6px]"
             style={{ gridTemplateColumns: `repeat(${Math.min(rest.length, 3)}, minmax(0, 1fr))` }}>
          {rest.map((item, i) => (
            <AnyFile key={i} item={item} onOpen={open} index={i + 1} count={photos.length} />
          ))}
        </div>
      )}

      {d.source && (
        <div className="relative mt-[14px] border-t border-dupontGoldLt/40 pt-[8px] font-marginNote font-semibold [font-variant:small-caps] tracking-[0.03em] text-[14px] text-gray-500">
          Source · {d.source}
        </div>
      )}
      {viewer}
    </div>
  );
}

export function PopUpBook({ post }) {
  const photos = images(post);
  const pages = linesOf(post, "pages");
  const d = details(post);
  const title = d.title || post.caption || `A story in ${photos.length} pages`;
  const [openAt, setOpenAt] = useState(null);
  if (photos.length < 3) return <Snapshot post={post} />;
  return (
    <>
      {/* The cover: a proper picture and room around it, so it invites you in
          rather than sitting tight (Sam, 2026-09-15). */}
      <div className="rounded-[12px] bg-dupontCream dark:bg-neutral-800 p-[18px]">
        <div className="relative mx-auto max-w-[520px]">
          <span aria-hidden="true" className="absolute inset-0 rounded-[8px] bg-[#fffdf6] shadow-sm -rotate-[3deg] -translate-x-[5px] translate-y-[4px]" />
          <span aria-hidden="true" className="absolute inset-0 rounded-[8px] bg-[#fffdf6] shadow-sm rotate-[2deg] translate-x-[5px] translate-y-[2px]" />
          <button type="button" onClick={() => setOpenAt(0)} aria-haspopup="dialog"
                  aria-label={`${title} — the cover`}
                  className="relative block w-full cursor-pointer border-0 bg-transparent p-0 m-0">
            <img src={cover(photos[0].url, 1040, 780)} alt="" loading="lazy"
                 className="block w-full aspect-[4/3] rounded-[8px] object-cover shadow-[0_12px_24px_-10px_rgba(40,32,8,0.45)]" />
          </button>
        </div>
        <div className="mt-[16px] text-center">
          <p className="m-0 font-display font-semibold text-[22px] leading-[1.25] text-dupontBlue dark:text-darkText break-words [overflow-wrap:anywhere]">{title}</p>
          <div className="mt-[3px] font-marginNote font-semibold text-[16px] text-gray-500">A story in {photos.length} pages</div>
          <button type="button" onClick={() => setOpenAt(0)} aria-haspopup="dialog"
                  className="mt-[12px] inline-flex items-center gap-[8px] rounded-full border border-dupontGold/70 bg-white dark:bg-neutral-900
                             px-[20px] py-[9px] font-blairBody text-[14px] font-semibold text-dupontBlue dark:text-darkText
                             shadow-sm hover:bg-dupontCream dark:hover:bg-neutral-700 transition-colors motion-reduce:transition-none">
            Open the book <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
      {openAt !== null && (
        <PhotoViewer photos={photos} lines={pages} title={title} counter="Page" pop startIndex={openAt} onClose={() => setOpenAt(null)} />
      )}
    </>
  );
}

export function Generations({ post }) {
  // A line-up: people oldest to youngest, or any set that belongs together
  // (Sam, 2026-09-15: three bouquets, or each daughter's pie).
  const photos = images(post);
  const people = details(post).people || [];
  const [open, viewer] = useBigView(
    photos,
    photos.map((_, i) => [people[i]?.label, people[i]?.name].filter(Boolean).join(" · ")),
    post.caption || "A line-up"
  );
  if (photos.length < 2) return <Snapshot post={post} />;
  const columns = Math.min(photos.length, 4);
  return (
    <div className="rounded-[10px] bg-dupontCream dark:bg-neutral-800 px-[12px] pt-[20px] pb-[16px]">
      {/* The column count rides in on a CSS VARIABLE, not on the inline
          grid-template-columns it used to set. An inline style beats every
          stylesheet rule, so the phone override below could never apply: a
          four-person line-up stayed four columns of about 68px while each
          portrait is a fixed 90px wide, and the photos spilled out of their
          columns (found by an audit of this PR, 2026-09-16). */}
      <ol className={"relative m-0 grid list-none gap-[10px] p-0 " +
                     "[grid-template-columns:repeat(var(--lineup-cols),minmax(0,1fr))] " +
                     "max-[460px]:[grid-template-columns:repeat(2,minmax(0,1fr))]"}
          style={{ "--lineup-cols": columns }}>
        <span aria-hidden="true" className="absolute left-[12%] right-[12%] top-[56px] h-px bg-dupontGold/70 max-[460px]:hidden" />
        {photos.map((photo, i) => (
          <li key={i} className="relative text-center">
            <Zoom onOpen={open} index={i} count={photos.length}>
              <img src={cover(photo.url, 360, 450)} alt={people[i]?.name || `Photo ${i + 1}`} loading="lazy"
                   className="relative mx-auto mb-[10px] block h-[112px] w-[90px] rounded-[50%] object-cover
                              border-[3px] border-white shadow-[0_0_0_1px_#b5a03e,0_10px_18px_-10px_rgba(40,32,8,0.45)]" />
            </Zoom>
            {people[i]?.label && (
              <b className="block font-marginNote font-semibold [font-variant:small-caps] tracking-[0.04em] text-[14px] leading-[1.15] text-dupontGoldDk dark:text-dupontGoldLt">
                {people[i].label}
              </b>
            )}
            {people[i]?.name && (
              <span className="mt-[2px] block font-marginNote font-semibold text-[15px] leading-[1.25] text-dupontBlue dark:text-darkText break-words [overflow-wrap:anywhere]">
                {people[i].name}
              </span>
            )}
          </li>
        ))}
      </ol>
      {viewer}
    </div>
  );
}
